// Timezone utilities for handling city-specific time zones
// Maps city codes to their respective timezone identifiers

// City code to timezone mapping
const CITY_TIMEZONES = {
  'ORL': 'America/New_York', // Orlando, FL
  'KIS': 'America/New_York', // Kissimmee, FL  
  'MCO': 'America/New_York', // Orlando International Airport area
  'NYC': 'America/New_York', // New York City
  'MIA': 'America/New_York', // Miami, FL
  'TPA': 'America/New_York', // Tampa, FL
  'JAX': 'America/New_York', // Jacksonville, FL
};

/**
 * Get timezone for a city code
 * @param {string} cityCode - City code (e.g., 'ORL', 'KIS')
 * @returns {string} Timezone identifier (e.g., 'America/New_York')
 */
export const getTimezoneForCity = (cityCode) => {
  return CITY_TIMEZONES[cityCode] || CITY_TIMEZONES['ORL']; // Default to Orlando
};

/**
 * Convert UTC date/time to city local time using Intl.DateTimeFormat
 * @param {Date|string} utcDateTime - UTC date/time
 * @param {string} cityCode - City code
 * @returns {Date} Local date/time for the city
 */
export const convertUTCToCityTime = (utcDateTime, cityCode) => {
  if (!utcDateTime) return null;
  
  const utcDate = new Date(utcDateTime);
  if (isNaN(utcDate.getTime())) return null;
  
  const timezone = getTimezoneForCity(cityCode);
  
  // Use Intl.DateTimeFormat to get the local time in the target timezone
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  // Format the UTC date to get local time components
  const formatted = formatter.format(utcDate);
  
  // Parse the formatted string (YYYY-MM-DD HH:mm:ss format)
  const [datePart, timePart] = formatted.split(', ');
  const [year, month, day] = datePart.split('-');
  const [hour, minute, second] = timePart.split(':');
  
  // Create a Date object by treating the local time as UTC, then return it
  // This ensures the Date object represents the correct local time without timezone shifts
  const localDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
  
  return localDate;
};

/**
 * Offset (ms) of a timezone from UTC at a given instant: local wall clock − UTC.
 * e.g. America/New_York in November → −5h.
 */
const timezoneOffsetMs = (instant, timezone) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(instant).map((p) => [p.type, p.value])
  );
  const wallClockAsUTC = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour), Number(parts.minute), Number(parts.second)
  );
  return wallClockAsUTC - instant.getTime();
};

/**
 * Convert a wall-clock date/time in the city's timezone to the real UTC instant.
 *
 * @param {string|Date} localDateTime
 *   - "YYYY-MM-DDTHH:mm[:ss]" (no timezone): read as the city's wall clock
 *   - Date, or a string ending in Z/offset: its UTC fields are the wall clock
 *     (how this app stores city time for the calendar — see convertUTCToCityTime)
 * @param {string} cityCode
 * @returns {Date|null} UTC instant, e.g. "2026-11-07T08:30" (Orlando) → 2026-11-07T13:30:00Z
 */
export const convertCityTimeToUTC = (localDateTime, cityCode) => {
  if (!localDateTime) return null;

  let wallClockAsUTC;
  const match = typeof localDateTime === 'string'
    && /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(localDateTime.trim());
  if (match) {
    const [, y, mo, d, h, mi, sec] = match;
    wallClockAsUTC = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(sec || 0));
  } else {
    const date = new Date(localDateTime);
    if (isNaN(date.getTime())) return null;
    wallClockAsUTC = date.getTime();
  }

  const timezone = getTimezoneForCity(cityCode);
  // UTC = wall clock − offset. Re-check the offset at the result so times
  // next to a daylight-saving change resolve correctly.
  let utc = wallClockAsUTC - timezoneOffsetMs(new Date(wallClockAsUTC), timezone);
  utc = wallClockAsUTC - timezoneOffsetMs(new Date(utc), timezone);
  return new Date(utc);
};

/**
 * Format date/time for display in city timezone
 * @param {Date|string} utcDateTime - UTC date/time
 * @param {string} cityCode - City code
 * @param {object} options - Formatting options
 * @returns {string} Formatted local time string
 */
export const formatCityTime = (utcDateTime, cityCode, options = {}) => {
  if (!utcDateTime) return '';
  
  const utcDate = new Date(utcDateTime);
  if (isNaN(utcDate.getTime())) return '';
  
  const timezone = getTimezoneForCity(cityCode);
  
  const defaultOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };
  
  const formatOptions = { ...defaultOptions, ...options };
  
  return new Intl.DateTimeFormat('en-US', formatOptions).format(utcDate);
};

/**
 * Get current time in city timezone
 * @param {string} cityCode - City code
 * @returns {Date} Current local time for the city
 */
export const getCurrentCityTime = (cityCode) => {
  const now = new Date();
  return convertUTCToCityTime(now.toISOString(), cityCode);
};

/**
 * Check if a date is in daylight saving time for a city
 * @param {Date|string} date - Date to check
 * @param {string} cityCode - City code
 * @returns {boolean} True if DST is active
 */
export const isDaylightSavingTime = (date, cityCode) => {
  if (!date) return false;
  
  const checkDate = new Date(date);
  if (isNaN(checkDate.getTime())) return false;
  
  const timezone = getTimezoneForCity(cityCode);
  
  // Get timezone offset in January (standard time) and July (potential DST)
  const jan = new Date(checkDate.getFullYear(), 0, 1);
  const jul = new Date(checkDate.getFullYear(), 6, 1);
  
  const janOffset = new Date(jan.toLocaleString('en-US', { timeZone: timezone })).getTimezoneOffset();
  const julOffset = new Date(jul.toLocaleString('en-US', { timeZone: timezone })).getTimezoneOffset();
  const currentOffset = new Date(checkDate.toLocaleString('en-US', { timeZone: timezone })).getTimezoneOffset();
  
  // DST is active if current offset is different from standard time (January)
  return currentOffset !== Math.max(janOffset, julOffset);
};

/**
 * Convert UTC date/time to city local time for FullCalendar
 * Creates a Date object representing local time in city timezone
 * @param {Date|string} utcDateTime - UTC date/time
 * @param {string} cityCode - City code
 * @returns {Date} Date object representing local time in city timezone
 */
export const convertUTCToCityTimeForCalendar = (utcDateTime, cityCode) => {
  if (!utcDateTime) return null;
  
  const utcDate = new Date(utcDateTime);
  if (isNaN(utcDate.getTime())) return null;
  
  const timezone = getTimezoneForCity(cityCode);
  
  // Create a formatter to get the local time components in the target timezone
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  // Format the UTC date to get local time components in the target timezone
  const formatted = formatter.format(utcDate);
  
  // Parse the formatted string (YYYY-MM-DD HH:mm:ss format)
  const [datePart, timePart] = formatted.split(', ');
  const [year, month, day] = datePart.split('-');
  const [hour, minute, second] = timePart.split(':');
  
  // Create a new Date object representing this local time
  // Note: We don't add 'Z' suffix - this creates a Date object in local timezone
  const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute), parseInt(second));
  
  return localDate;
};

/**
 * Convert event object times from UTC to city timezone for FullCalendar
 * This creates Date objects that represent local time in the city timezone
 * @param {object} event - Event object with start/end times
 * @param {string} cityCode - City code
 * @returns {object} Event with converted times
 */
export const convertEventTimesToCityTimeForCalendar = (event, cityCode) => {
  if (!event) return event;
  
  const convertedEvent = { ...event };
  
  if (event.start) {
    const localStart = convertUTCToCityTimeForCalendar(event.start, cityCode);
    convertedEvent.start = localStart;
  }
  
  if (event.end) {
    const localEnd = convertUTCToCityTimeForCalendar(event.end, cityCode);
    convertedEvent.end = localEnd;
  }
  
  return convertedEvent;
};

/**
 * Convert multiple events from UTC to city timezone for FullCalendar
 * @param {Array} events - Array of event objects
 * @param {string} cityCode - City code
 * @returns {Array} Events with converted times
 */
export const convertEventsTimesToCityTimeForCalendar = (events, cityCode) => {
  if (!Array.isArray(events)) return events;
  
  return events.map(event => convertEventTimesToCityTimeForCalendar(event, cityCode));
};

/**
 * Convert event object times from UTC to city timezone
 * @param {object} event - Event object with start/end times
 * @param {string} cityCode - City code
 * @returns {object} Event with converted times
 */
export const convertEventTimesToCityTime = (event, cityCode) => {
  if (!event) return event;
  
  const convertedEvent = { ...event };
  
  if (event.start) {
    const localStart = convertUTCToCityTime(event.start, cityCode);
    convertedEvent.start = localStart ? localStart.toISOString() : event.start;
  }
  
  if (event.end) {
    const localEnd = convertUTCToCityTime(event.end, cityCode);
    convertedEvent.end = localEnd ? localEnd.toISOString() : event.end;
  }
  
  return convertedEvent;
};





